"use client";
import React, { useId, useState } from "react";
import Image from "next/image";
import {
  BookOpen,
  Check,
  MessageSquare,
  Monitor,
  TrendingUp,
} from "lucide-react";
import type { IFormationDetail, ITrainer } from "@/types/formation";
import { METHOD_LABELS, EVALUATION_LABELS } from "@/constants/formation";
import DetailedProgram from "./DetailedProgram";
import RichText from "../Common/RichText";
import Link from "next/link";

interface FicheObjectivesProps {
  formation: IFormationDetail;
}

const DEFAULT_RESOURCES = [
  "Plateforme interactive",
  "Supports de formation",
  "Contenus régulièrement mis à jour",
  "Suivi post-formation 3 mois",
];

/* Resources are free text (formation.documents), so there is nothing to key an
   icon off — the list cycles through these in order, as the design does. */
const RESOURCE_ICONS = [Monitor, BookOpen, TrendingUp, MessageSquare];

const IMPROVEMENT_STEPS = [
  { title: "Recueil des appréciations", items: ["Formulaire de contact"] },
  {
    title: "Suivi",
    items: [
      "Questionnaires, Enquêtes de satisfaction",
      "Cadre de gestion des réclamations",
    ],
  },
  {
    title: "Implémentation",
    items: [
      "Mise à jour des formations selon les retours des apprenants",
      "Et l’évolution des métiers",
    ],
  },
];

/* Past this many characters the bio no longer fits the 4 lines the card leaves
   for it, so it gets clamped behind the "En savoir plus" toggle. */
const BIO_CLAMP_LENGTH = 260;

type TabId = "objectifs" | "prerequis" | "public";

/** One of the three outlined cards of the details grid. */
const DetailCard: React.FC<{
  title: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, className = "", children }) => (
  <div
    className={`flex flex-col bg-white rounded-2xl border border-[#40BFDE] md:p-6 p-5 ${className}`}
  >
    <h3 className="text-[17px] font-bold leading-7 text-[#1B365E]">{title}</h3>
    {children}
  </div>
);

/** Small print inside the details cards — was 9px, below the legibility floor. */
const FINE_PRINT = "text-[12px] leading-4.5 text-[#1B365E]";

/** One trainer of the "Votre Formateur" card: grey identity block + side actions. */
const TrainerCard: React.FC<{ trainer: ITrainer }> = ({ trainer }) => {
  const [expanded, setExpanded] = useState(false);

  const bio = trainer.bio ?? "";
  const isLong = bio.length > BIO_CLAMP_LENGTH;

  return (
    <li className="flex flex-col items-center justify-center gap-5 lg:flex-row lg:gap-10">
      {/* min-h, not h: a fixed 160px cut the stacked mobile layout off, and an
          expanded bio out of any layout. */}
      <div className="flex w-full max-w-150 flex-col items-center gap-4 rounded-2xl bg-[#EFEFEF] px-5 py-5 md:min-h-40 md:flex-row md:gap-6 md:px-8 md:py-6">
        <div className="flex shrink-0 flex-col items-center gap-2 md:w-24">
          {trainer.photo_url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={trainer.photo_url}
              alt=""
              aria-hidden="true"
              width={84}
              height={84}
              loading="lazy"
              decoding="async"
              className="size-16 rounded-full object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className="flex size-16 items-center justify-center rounded-full bg-[#40BFDE] font-red-hat text-[24px] font-light leading-none text-white"
            >
              {trainer.full_name.charAt(0).toUpperCase()}
            </span>
          )}
          <p className="text-center text-[13px] font-bold leading-5 text-[#1B365E]">
            {trainer.full_name}
          </p>
        </div>

        <div className="min-w-0 text-center md:text-left">
          {trainer.title && (
            <p className="mb-1 text-[13px] font-bold leading-5 text-[#40BFDE]">
              {trainer.title}
            </p>
          )}
          <p
            className={`text-[13px] leading-5 text-[#1B365E] ${
              isLong && !expanded ? "line-clamp-4" : ""
            }`}
          >
            {bio || "Biographie non renseignée."}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              className="mt-2 inline-flex cursor-pointer items-center gap-2 text-[13px] font-bold leading-5 text-[#1B365E] hover:text-[#40BFDE] transition-colors"
            >
              <Image
                src="/assets/icons/arrow-right-circle.svg"
                alt=""
                aria-hidden="true"
                width={16}
                height={16}
                className={`shrink-0 transition-transform ${
                  expanded ? "rotate-90" : ""
                }`}
              />
              {expanded ? "Réduire la biographie" : "Lire la biographie"}
            </button>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-center gap-3">
        <span className="flex size-14 items-center justify-center rounded-full border border-[#B6C93E]">
          <Image
            src="/assets/icons/id-card.svg"
            alt="card identité"
            title="card identité"
            aria-hidden="true"
            width={26}
            height={35}
            className="w-5 shrink-0"
          />
        </span>
        <Link
          href="/contact"
          className="inline-flex min-h-11 flex-row items-center gap-1.5 text-[14px] font-bold text-[#1B365E] hover:text-[#40BFDE] transition-colors"
        >
          <svg className="size-4 shrink-0" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8ZM4.75 7.25C4.33579 7.25 4 7.58579 4 8C4 8.41421 4.33579 8.75 4.75 8.75L9.43934 8.75L8.21967 9.96967C7.92678 10.2626 7.92678 10.7374 8.21967 11.0303C8.51256 11.3232 8.98744 11.3232 9.28033 11.0303L11.7803 8.53033C12.0732 8.23744 12.0732 7.76256 11.7803 7.46967L9.28033 4.96967C8.98744 4.67678 8.51256 4.67678 8.21967 4.96967C7.92678 5.26256 7.92678 5.73744 8.21967 6.03033L9.43934 7.25L4.75 7.25Z" fill="currentColor" />
          </svg>
          Contacter
        </Link>
      </div>
    </li>
  );
};

const FicheObjectives: React.FC<FicheObjectivesProps> = ({ formation }) => {
  const competencies = formation.specific_objectives;

  const resources =
    formation.documents.length > 0
      ? formation.documents.map((doc) => doc.name)
      : DEFAULT_RESOURCES;

  /* The detail payload carries trainers both as a relation and as flat
     trainer_* fields (older formations only have the latter). */
  const trainers: ITrainer[] =
    formation.trainers.length > 0
      ? formation.trainers
      : formation.trainer_name
        ? [
            {
              id: 0,
              full_name: formation.trainer_name,
              title: formation.trainer_title,
              bio: formation.trainer_bio,
              photo_url: null,
            },
          ]
        : [];

  const methodLabels = formation.methods.map((m) => METHOD_LABELS[m] ?? m);
  const evaluationLabels = formation.evaluation_methods.map(
    (e) => EVALUATION_LABELS[e] ?? e,
  );

  /* --- Tabs (top card) --- */
  const [activeTab, setActiveTab] = useState<TabId>("objectifs");

  const tabs: { id: TabId; label: string }[] = [
    { id: "objectifs", label: "Objectifs pédagogiques" },
    { id: "prerequis", label: "Pré-requis" },
    ...(formation.target_audience
      ? [{ id: "public" as TabId, label: "Public visé" }]
      : []),
  ];

  /* Instance-scoped so the ids stay unique no matter how often the section is
     mounted (see PriceCard, which the page renders twice). */
  const uid = useId();
  const tabId = (id: TabId) => `${uid}-tab-${id}`;
  const tabPanelId = (id: TabId) => `${uid}-panel-${id}`;

  /* Left/Right move between tabs, Home/End jump to the ends — what a tablist is
     expected to answer to once the buttons carry role="tab". */
  const handleTabKeys = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const offset =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    let next: number | null = null;
    if (offset !== 0) {
      const current = tabs.findIndex((tab) => tab.id === activeTab);
      next = (current + offset + tabs.length) % tabs.length;
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = tabs.length - 1;
    }

    if (next === null) return;
    event.preventDefault();
    setActiveTab(tabs[next].id);
    document.getElementById(tabId(tabs[next].id))?.focus();
  };

  const panelProps = (id: TabId) => ({
    id: tabPanelId(id),
    role: "tabpanel" as const,
    "aria-labelledby": tabId(id),
    tabIndex: 0,
  });

  return (
    <section className="pt-14 pb-6 md:pt-18 md:pb-10">
      <div className="px-4">
        {/* Tabbed Card */}
        <div className="relative md:mb-8 mb-6">
          <div className="bg-white rounded-3xl md:px-16 px-4 md:pt-20 pt-12 md:pb-20 pb-8 border border-[#40BFDE]">
            <div
              role="tablist"
              aria-label="Informations sur la formation"
              onKeyDown={handleTabKeys}
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 flex w-full items-center justify-start md:justify-center gap-2 md:gap-6 lg:gap-3 overflow-x-auto no-scrollbar px-4 py-3"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  id={tabId(tab.id)}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab(tab.id)}
                  aria-selected={activeTab === tab.id}
                  aria-controls={tabPanelId(tab.id)}
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  className={`shrink-0 md:px-8 px-4 md:py-4 py-2.5 rounded-full font-bold md:text-[15px] text-[11px] leading-4.5 whitespace-nowrap transition-colors cursor-pointer shadow-[1px_2px_8px_2px_#0000001A] ${
                    activeTab === tab.id
                      ? "bg-[#1B365E] text-white border-2 border-[#C7F8FF]"
                      : "bg-white text-[#1B365E] border border-[#EFEFEF] hover:bg-[#C7F8FF]"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Objectifs pédagogiques */}
            {activeTab === "objectifs" && (
              <div {...panelProps("objectifs")}>
                <p className="font-red-hat font-light text-[#1B365E] text-center md:text-[36px] text-[22px] md:leading-11 leading-8 md:mb-11 mb-8">
                  Vous obtiendrez les compétences suivantes :
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                  {competencies.map((text, i) => (
                    /* Fully rounded, bottom + right rule only — the open outline
                       the design uses for these. */
                    <div
                      key={i}
                      className="flex items-center gap-4 border-b border-r border-[#D9D9D9] rounded-[40px] md:px-6 px-4 py-3"
                    >
                      <span
                        aria-hidden="true"
                        className="font-red-hat font-light text-[#1B365E] md:text-[40px] text-[30px] leading-none shrink-0"
                      >
                        {i + 1}
                      </span>

                      <p className="text-[#1B365E] text-[14px] leading-6">
                        {text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pré-requis */}
            {activeTab === "prerequis" && (
              <div {...panelProps("prerequis")} className="text-center">
                {formation.prerequisites ? (
                  <RichText
                    html={formation.prerequisites}
                    className="text-[#1B365E] text-[15px] md:text-[16px] leading-6 md:leading-7 max-w-2xl mx-auto wrap-break-word [&_ol]:inline-block [&_ol]:text-left [&_ul]:inline-block [&_ul]:text-left"
                  />
                ) : (
                  <p className="text-[#1B365E] text-[15px] md:text-[16px] leading-6 md:leading-7 max-w-2xl mx-auto">
                    Aucun prérequis spécifique.
                  </p>
                )}
              </div>
            )}

            {/* Public visé */}
            {activeTab === "public" && formation.target_audience && (
              <div {...panelProps("public")} className="text-center">
                <p className="font-red-hat font-light text-[#1B365E] md:text-[36px] text-[22px] md:leading-11 leading-8 md:mb-11 mb-8">
                  À qui est adressée cette formation ?
                </p>

                <RichText
                  html={formation.target_audience}
                  className="text-[#1B365E] text-[15px] md:text-[16px] leading-6 md:leading-7 max-w-2xl mx-auto wrap-break-word [&_ol]:inline-block [&_ol]:text-left [&_ul]:inline-block [&_ul]:text-left"
                />
              </div>
            )}
          </div>
        </div>
        <DetailedProgram formation={formation} />
        {/* Details Grid */}
        <div className="md:bg-[#EFEFEF] py-6 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
            {/* Ressources pédagogiques */}
            <DetailCard title="Ressources pédagogiques">
              <ul className="mt-6 space-y-4">
                {resources.map((item, i) => {
                  const Icon = RESOURCE_ICONS[i % RESOURCE_ICONS.length];
                  return (
                    <li key={item} className="flex items-center gap-4">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#EDEDED]">
                        <Icon
                          className="size-4 text-[#1B365E]"
                          strokeWidth={1.75}
                          aria-hidden="true"
                        />
                      </span>
                      <span className="text-[15px] leading-6 text-[#1B365E]">
                        {item}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </DetailCard>

            {/* Approche pédagogique */}
            <DetailCard title="Approche pédagogique">
              <ul className="mt-5 space-y-3">
                {methodLabels.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[15px] leading-6 text-[#1B365E]"
                  >
                    <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-[#1B365E]" />
                    {item}
                  </li>
                ))}
              </ul>
              {formation.methods_detail && (
                <RichText
                  html={formation.methods_detail}
                  className="mt-4 text-[15px] leading-6 text-[#1B365E] wrap-break-word"
                />
              )}
            </DetailCard>

            {/* Modalités de suivi — the tall card, spanning both rows */}
            <DetailCard
              className="lg:row-span-2"
              title="Modalités de suivi et d’amélioration"
            >
              <p className={`mt-2 font-bold ${FINE_PRINT}`}>
                Processus d’amélioration continue chez Spot Formations
              </p>

              <ol className="mt-5 space-y-6">
                {IMPROVEMENT_STEPS.map((step, i) => (
                  <li key={step.title}>
                    <p className="text-[15px] font-bold leading-6 text-[#1B365E]">
                      {i + 1}. {step.title}
                    </p>
                    <ul className="mt-2 space-y-2 ps-1">
                      {step.items.map((item) => (
                        <li
                          key={item}
                          className={`flex items-start gap-2 ${FINE_PRINT}`}
                        >
                          <span className="mt-1.5 size-1 shrink-0 rounded-full bg-[#1B365E]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>

              {/* mt-auto pins the illustration to the foot of the tall card —
                  without it the card's extra row left a gap under it. */}
              <div className="flex justify-center pt-8">
                <Image
                  src="/assets/icons/verification.svg"
                  alt="verification"
                  title="verification"
                  aria-hidden="true"
                  width={58}
                  height={64}
                  className="shrink-0"
                />
              </div>
            </DetailCard>

            {/* Évaluation & Validation — filled, under the two left columns */}
            <div className="lg:col-span-2 bg-[#C7F8FF] rounded-2xl md:p-8 p-5">
              <h3 className="font-red-hat font-light text-[#1B365E] text-[20px] md:text-[22px] leading-8">
                Évaluation &amp; Validation
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 md:gap-8 gap-6 md:mt-8 mt-6">
                <div>
                  <p className="text-[15px] font-bold leading-6 text-[#1B365E]">
                    Évaluation des compétences
                  </p>
                  <ul className="mt-4 space-y-2.5">
                    {(evaluationLabels.length > 0
                      ? evaluationLabels
                      : ["Non renseigné"]
                    ).map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2.5 text-[15px] leading-6 text-[#1B365E]"
                      >
                        <Check
                          className="mt-1 size-4 shrink-0 text-[#FC7F16]"
                          strokeWidth={3}
                          aria-hidden="true"
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-[15px] font-bold leading-6 text-[#1B365E]">
                    Validation
                  </p>
                  <div className="mt-4 flex items-center gap-4 rounded-xl border border-[#40BFDE] bg-white md:px-5 px-4 py-4">
                    <Image
                      src="/assets/icons/id-card.svg"
                      alt="card identité"
                      title="card identité"
                      aria-hidden="true"
                      width={26}
                      height={35}
                      className="w-6.5 shrink-0"
                    />
                    {formation.has_certification ? (
                      <p className="min-w-0 text-[14px] leading-5.5 text-[#1B365E]">
                        Certification de réalisation
                        <br />
                        &ldquo;{formation.certification_name}&rdquo;
                      </p>
                    ) : (
                      <p className="min-w-0 text-[14px] leading-5.5 text-[#1B365E]">
                        Attestation de fin de formation et attestation de
                        présence.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Votre Formateur */}
        {trainers.length > 0 && (
          <div className="mt-6 rounded-3xl border border-[#40BFDE] bg-white p-5 md:mt-8 md:p-8">
            <h3 className="text-center font-red-hat text-[22px] font-light leading-8 text-[#1B365E] md:text-[36px] md:leading-11">
              {trainers.length > 1 ? "Vos Formateurs" : "Votre Formateur"}
            </h3>

            <ul className="mt-6 space-y-5 md:mt-8">
              {trainers.map((trainer) => (
                <TrainerCard key={trainer.id} trainer={trainer} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default FicheObjectives;